"use client";

import type { Element, Root, RootContent, Text } from "hast";
import {
  Streamdown,
  defaultRehypePlugins,
  type LinkSafetyModalProps,
  type StreamdownProps,
} from "streamdown";
import { CopyButton } from "@tracify/ui/copy-button";
import { Button } from "@tracify/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tracify/ui/dialog";
import type { ReactNode } from "react";

function textContent(node: RootContent): string {
  if (node.type === "text") return (node as Text).value;
  if (node.type === "element" && node.tagName === "br") return "\n";
  return "children" in node ? node.children.map(textContent).join("") : "";
}

function tableText(table: Element): string {
  const rows: string[] = [];
  const visit = (node: Element) => {
    if (node.tagName === "tr") {
      rows.push(
        node.children
          .filter(
            (child): child is Element =>
              child.type === "element" &&
              (child.tagName === "th" || child.tagName === "td")
          )
          .map(textContent)
          .join("\t")
      );
    } else {
      node.children.forEach((child) => {
        if (child.type === "element") visit(child);
      });
    }
  };
  visit(table);
  return rows.join("\n");
}

// Keep Streamdown's highlighting, diagrams, downloads and fullscreen controls,
// while routing its code/diagram/table copy actions through the dashboard button.
export function rehypeCopyableBlocks() {
  return (tree: Root) => {
    const visit = (parent: Root | Element) => {
      parent.children = parent.children.map((node) => {
        if (node.type !== "element") return node;
        if (node.tagName === "pre" || node.tagName === "table") {
          return {
            type: "element",
            tagName: "copyable-block",
            properties: {
              copyValue:
                node.tagName === "table" ? tableText(node) : textContent(node),
              copyLabel: node.tagName === "table" ? "table" : "code",
            },
            children: [node],
          };
        }
        visit(node);
        return node;
      });
    };
    visit(tree);
  };
}

function CopyableBlock({
  children,
  copyValue,
  copyLabel,
}: Record<string, unknown>) {
  return (
    <div className="flex min-w-0 items-start gap-1">
      <div className="min-w-0 flex-1">{children as ReactNode}</div>
      <CopyButton
        value={String(copyValue ?? "")}
        copyLabel={String(copyLabel ?? "value")}
        className="mt-4"
      />
    </div>
  );
}

const rehypePlugins = [
  ...Object.values(defaultRehypePlugins),
  rehypeCopyableBlocks,
];
const components = { "copyable-block": CopyableBlock };
const controls = {
  code: { copy: false },
  table: { copy: false },
  mermaid: { copy: false },
};

function LinkSafetyDialog({
  isOpen,
  onClose,
  onConfirm,
  url,
}: LinkSafetyModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Open external link?</DialogTitle>
          <DialogDescription>
            You’re about to visit an external website.
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-w-0 items-center gap-2 rounded-lg bg-muted p-3">
          <span className="min-w-0 flex-1 break-all text-sm">{url}</span>
          <CopyButton value={url} copyLabel="link" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            Open link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DashboardStreamdown(props: StreamdownProps) {
  return (
    <Streamdown
      {...props}
      rehypePlugins={rehypePlugins}
      components={{ ...props.components, ...components }}
      controls={controls}
      linkSafety={{
        enabled: true,
        ...props.linkSafety,
        renderModal: LinkSafetyDialog,
      }}
    />
  );
}
